import {
  api,
  asApiError,
  byId,
  colours,
  displayHoldingName,
  errorMessage,
  escapeCsv,
  escapeHtml,
  formatNumber,
  hideError,
  holdingKey,
  money,
  parseCsv,
  parseNumber,
  portfolioPercentage,
  resolveAssetClass,
  resolveSymbol,
  setDiagnostics,
  showError,
  state,
} from './core';
import {
  classifyAssetClass,
  classifyHolding,
  emptyValues,
  TARGET_CLASS_LABELS,
  TARGET_CLASSES,
} from './rebalance';
import { syncRebalanceAllocation } from './rebalance-ui';
import type { PricedHolding, PricesResponse } from './types';

interface HoldingGroup {
  key: string;
  rows: PricedHolding[];
  first: PricedHolding;
  totalQuantity: number;
  totalValue: number | null;
  weightedPrice: number | null;
}

// Fixed colours for the four core asset classes in the "Asset classes" chart.
const ASSET_CLASS_COLOURS: Record<string, string> = {
  gold: '#eab308', // yellow
  stocks: '#dc2626', // red
  bonds: '#6b7280', // grey
  cash: '#16a34a', // green
};

function assetClassColour(name: string, index: number): string {
  const cls = classifyAssetClass(name);
  return (cls ? ASSET_CLASS_COLOURS[cls] : undefined) ?? colours[index % colours.length] ?? '#6b7280';
}

// Holdings-table sorting. Columns match the header order in index.html:
// 0 Account, 1 Holding, 2 Ticker, 3 Quantity, 4 Price, 5 Value, 6 Portfolio %,
// 7 Asset class, 8 Status. Numeric columns default to descending.
type SortDir = 'asc' | 'desc';
const NUMERIC_COLUMNS = new Set([3, 4, 5, 6]);
let holdingSort: { column: number; dir: SortDir } = { column: 5, dir: 'desc' };
let holdingsContext: { currency: string; total: number } | null = null;

function groupSortValue(group: HoldingGroup, column: number): string | number {
  switch (column) {
    case 0: return (group.first.Account || 'Unspecified').toLowerCase();
    case 1: return displayHoldingName(group.first).toLowerCase();
    case 2: return (group.first.symbol || '').toLowerCase();
    case 3: return group.totalQuantity;
    case 4: return group.weightedPrice ?? -Infinity;
    case 5: return group.totalValue ?? -Infinity;
    case 6: return group.totalValue ?? -Infinity;
    case 7: return (group.first.resolvedAssetClass || 'Other').toLowerCase();
    case 8: return (group.totalValue === null ? group.first.error || 'Unpriced' : group.first.source).toLowerCase();
    default: return group.totalValue ?? -Infinity;
  }
}

function sortHoldingGroups(groups: HoldingGroup[]): void {
  const { column, dir } = holdingSort;
  const factor = dir === 'asc' ? 1 : -1;
  groups.sort((a, b) => {
    const left = groupSortValue(a, column);
    const right = groupSortValue(b, column);
    const cmp = typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right));
    return cmp * factor;
  });
}

function holdingHeaders(): NodeListOf<HTMLTableCellElement> {
  return document.querySelectorAll<HTMLTableCellElement>('#holdingsSection thead th');
}

function updateHoldingSortIndicators(): void {
  holdingHeaders().forEach((th, index) => {
    th.setAttribute('aria-sort', holdingSort.column === index
      ? (holdingSort.dir === 'asc' ? 'ascending' : 'descending')
      : 'none');
  });
}

export async function checkApi(): Promise<void> {
  try {
    const response = await api<{ ok: true; version: string }>('/api/test');
    const status = byId<HTMLElement>('apiStatus');
    status.textContent = `Pricing service online · v${response.version}`;
    status.className = 'status ok';
    setDiagnostics(response);
  } catch (error) {
    const apiError = asApiError(error);
    const status = byId<HTMLElement>('apiStatus');
    status.textContent = 'Pricing service unavailable';
    status.className = 'status bad';
    setDiagnostics(apiError.details ?? { error: apiError.message });
  }
}

export async function copyDiagnostics(): Promise<void> {
  const text = byId<HTMLElement>('diagnosticsText').textContent ?? '';
  const status = byId<HTMLElement>('copyDiagnosticsStatus');

  try {
    await navigator.clipboard.writeText(text);
    status.textContent = 'Copied';
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    status.textContent = copied ? 'Copied' : 'Copy failed';
  }

  window.setTimeout(() => { status.textContent = ''; }, 1800);
}

export async function loadFile(file: File): Promise<void> {
  state.rows = parseCsv(await file.text());
  byId<HTMLElement>('fileStatus').textContent = `Ready: ${file.name} · ${state.rows.length} holdings`;
}

export async function analyse(): Promise<void> {
  hideError();
  if (state.rows.length === 0) {
    showError('Choose a CSV first.');
    return;
  }

  const reporting = byId<HTMLSelectElement>('reportingCurrency').value;
  const symbols = [...new Set(state.rows.map(resolveSymbol).filter(Boolean))];
  const button = byId<HTMLButtonElement>('analyseButton');
  button.disabled = true;
  button.textContent = 'Updating…';

  try {
    const response = await api<PricesResponse>(
      `/api/prices?symbols=${encodeURIComponent(symbols.join(','))}&reporting=${reporting}`,
    );
    setDiagnostics(response);

    state.results = state.rows.map((row): PricedHolding => {
      const quantity = parseNumber(row.Quantity);
      const manualPrice = parseNumber(row.ManualPrice);
      const symbol = resolveSymbol(row);
      const quote = response.prices[symbol];
      const source = manualPrice !== null ? 'Manual' : quote?.ok ? 'Yahoo' : 'Unavailable';
      const price = manualPrice !== null ? manualPrice : quote?.ok ? quote.price : null;
      const currency = (row.Currency ?? (quote?.ok ? quote.currency : null) ?? reporting).toUpperCase();
      const fx = response.fx[currency] ?? (currency === reporting ? 1 : null);
      const value = quantity !== null && price !== null && fx !== null ? quantity * price * fx : null;

      return {
        ...row,
        quantity,
        symbol,
        price,
        currency,
        fx,
        value,
        source,
        error: quote && !quote.ok ? quote.error : '',
        resolvedAssetClass: resolveAssetClass(row),
      };
    });

    render(reporting);
  } catch (error) {
    const apiError = asApiError(error);
    showError(`Could not update prices: ${apiError.message}`);
    setDiagnostics(apiError.details ?? { error: apiError.message, stack: apiError.stack });
  } finally {
    button.disabled = false;
    button.textContent = 'Update portfolio';
  }
}

function render(currency: string): void {
  const priced = state.results.filter((row): row is PricedHolding & { value: number } =>
    row.value !== null && Number.isFinite(row.value),
  );
  const total = priced.reduce((sum, row) => sum + row.value, 0);

  byId<HTMLElement>('totalValue').textContent = money(total, currency);
  byId<HTMLElement>('pricedCount').textContent = String(priced.length);
  byId<HTMLElement>('unpricedCount').textContent = String(state.results.length - priced.length);
  byId<HTMLElement>('refreshTime').textContent = new Date().toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const classes = group(priced, (row) => row.resolvedAssetClass || 'Other');
  const accounts = group(priced, (row) => row.Account || 'Unspecified');
  renderPie(classes, total, currency);
  renderBars(accounts, total, currency);
  renderAllocation(priced, total, currency);
  renderHoldings(currency, total);
  syncRebalanceAllocation(priced, currency);

  ['summary', 'visuals', 'allocationSection', 'holdingsSection'].forEach((id) =>
    byId<HTMLElement>(id).classList.remove('hidden'),
  );
}

function group(rows: Array<PricedHolding & { value: number }>, key: (row: PricedHolding) => string): Array<[string, number]> {
  const result: Record<string, number> = {};
  for (const row of rows) {
    const groupKey = key(row);
    result[groupKey] = (result[groupKey] ?? 0) + row.value;
  }
  return Object.entries(result).sort((left, right) => right[1] - left[1]);
}

function renderPie(entries: Array<[string, number]>, total: number, currency: string): void {
  const canvas = byId<HTMLCanvasElement>('allocationChart');
  const context = canvas.getContext('2d');
  if (!context) return;

  const centreX = 260;
  const centreY = 180;
  const radius = 125;
  const innerRadius = 72;
  context.clearRect(0, 0, canvas.width, canvas.height);
  let start = -Math.PI / 2;

  entries.forEach(([name, value], index) => {
    const end = start + (total > 0 ? value / total : 0) * Math.PI * 2;
    context.beginPath();
    context.arc(centreX, centreY, radius, start, end);
    context.arc(centreX, centreY, innerRadius, end, start, true);
    context.closePath();
    context.fillStyle = assetClassColour(name, index);
    context.fill();
    start = end;
  });

  context.fillStyle = '#111827';
  context.textAlign = 'center';
  // Shrink the centre amount so large totals stay inside the inner circle
  // instead of spilling over the ring.
  const centreLabel = total ? money(total, currency) : '—';
  const maxLabelWidth = innerRadius * 2 - 16;
  let labelSize = 24;
  context.font = `700 ${labelSize}px -apple-system, sans-serif`;
  while (labelSize > 13 && context.measureText(centreLabel).width > maxLabelWidth) {
    labelSize -= 1;
    context.font = `700 ${labelSize}px -apple-system, sans-serif`;
  }
  context.fillText(centreLabel, centreX, centreY + 5);
  context.font = '14px -apple-system, sans-serif';
  context.fillStyle = '#6b7280';
  context.fillText('priced total', centreX, centreY + 29);

  byId<HTMLElement>('allocationLegend').innerHTML = entries.map(([name, value], index) =>
    `<span class="legend-item"><i class="swatch" style="background:${assetClassColour(name, index)}"></i>${escapeHtml(name)} ${total ? ((value / total) * 100).toFixed(1) : 0}%</span>`,
  ).join('');
}

function renderBars(entries: Array<[string, number]>, total: number, currency: string): void {
  byId<HTMLElement>('accountBars').innerHTML = entries.map(([name, value]) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return `<div><div class="bar-label"><span>${escapeHtml(name)}</span><strong>${money(value, currency)} · ${percentage.toFixed(1)}%</strong></div><div class="bar-track"><div class="bar-fill" style="width:${percentage}%"></div></div></div>`;
  }).join('');
}

function renderAllocation(
  priced: Array<PricedHolding & { value: number }>,
  total: number,
  currency: string,
): void {
  // Always show the four core asset classes — even at 0% — so the breakdown
  // lines up with the rebalancing targets. Anything that can't be bucketed is
  // summed into a trailing "Other" row only when it actually holds value.
  const buckets = emptyValues();
  let other = 0;
  for (const row of priced) {
    const cls = classifyHolding(row.resolvedAssetClass, [row.ISIN, row.Ticker, row.symbol]);
    if (cls) buckets[cls] += row.value;
    else other += row.value;
  }

  const entries: Array<[string, number]> = TARGET_CLASSES.map((cls) => [TARGET_CLASS_LABELS[cls], buckets[cls]]);
  if (other > 0.005) entries.push(['Other', other]);

  byId<HTMLElement>('allocationRows').innerHTML = entries.map(([name, value]) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return `<tr><td>${escapeHtml(name)}</td><td>${money(value, currency)}</td><td>${percentage.toFixed(2)}%</td></tr>`;
  }).join('');
}

function buildHoldingGroups(): HoldingGroup[] {
  const grouped = new Map<string, PricedHolding[]>();
  for (const row of state.results) {
    const key = holdingKey(row);
    const rows = grouped.get(key) ?? [];
    rows.push(row);
    grouped.set(key, rows);
  }

  return [...grouped.entries()].flatMap(([key, rows]) => {
    const first = rows[0];
    if (!first) return [];
    const pricedRows = rows.filter((row): row is PricedHolding & { value: number } =>
      row.value !== null && Number.isFinite(row.value),
    );
    const totalQuantity = rows.reduce((sum, row) => sum + (row.quantity ?? 0), 0);
    const totalValue = pricedRows.length > 0 ? pricedRows.reduce((sum, row) => sum + row.value, 0) : null;
    const pricedQuantity = rows.reduce((sum, row) =>
      row.price !== null && row.quantity !== null ? sum + row.quantity : sum, 0);
    const weightedPrice = pricedQuantity > 0
      ? rows.reduce((sum, row) =>
          row.price !== null && row.quantity !== null ? sum + row.price * row.quantity : sum, 0) / pricedQuantity
      : null;
    return [{ key, rows, first, totalQuantity, totalValue, weightedPrice }];
  }).sort((left, right) => (right.totalValue ?? -Infinity) - (left.totalValue ?? -Infinity));
}

function renderHoldings(reporting: string, total: number): void {
  holdingsContext = { currency: reporting, total };
  let html = '';

  const groups = buildHoldingGroups();
  sortHoldingGroups(groups);
  groups.forEach((group, index) => {
    const { key, rows, first, totalQuantity, totalValue, weightedPrice } = group;
    const duplicate = rows.length > 1;
    const groupId = `holding-group-${index}`;
    const expanded = duplicate && state.expandedHoldings.has(key);
    const statusClass = totalValue === null ? 'bad' : first.source === 'Manual' ? 'manual' : 'ok';
    const statusText = totalValue === null ? first.error || 'Unpriced' : first.source;
    const toggle = duplicate
      ? `<button class="holding-toggle" type="button" aria-expanded="${expanded}" aria-controls="${groupId}" aria-label="${expanded ? 'Collapse' : 'Expand'} account details" data-group="${groupId}" data-key="${escapeHtml(key)}"><span aria-hidden="true">›</span></button>`
      : '';

    html += `<tr class="${duplicate ? 'holding-parent' : ''}">
      <td><span class="holding-account-cell">${toggle}<span>${duplicate ? rows.length : escapeHtml(first.Account || 'Unspecified')}</span></span></td>
      <td>${escapeHtml(displayHoldingName(first))}</td>
      <td>${escapeHtml(first.symbol || '—')}</td>
      <td>${rows.every((row) => row.quantity === null) ? '—' : formatNumber(totalQuantity)}</td>
      <td>${weightedPrice === null ? '—' : `${formatNumber(weightedPrice)} ${escapeHtml(first.currency)}`}</td>
      <td>${totalValue === null ? '—' : money(totalValue, reporting)}</td>
      <td>${totalValue === null ? '—' : portfolioPercentage(totalValue, total)}</td>
      <td>${escapeHtml(first.resolvedAssetClass || 'Other')}</td>
      <td><span class="pill ${statusClass}">${escapeHtml(statusText)}</span></td>
    </tr>`;

    if (duplicate) {
      rows.forEach((row) => {
        html += `<tr class="holding-child${expanded ? '' : ' hidden'}" data-parent="${groupId}">
          <td class="holding-child-account">${escapeHtml(row.Account || 'Unspecified')}</td>
          <td></td><td></td>
          <td>${row.quantity === null ? '—' : formatNumber(row.quantity)}</td>
          <td></td>
          <td>${row.value === null ? '—' : money(row.value, reporting)}</td>
          <td>${row.value === null ? '—' : portfolioPercentage(row.value, total)}</td>
          <td></td><td></td>
        </tr>`;
      });
    }
  });

  byId<HTMLElement>('holdingRows').innerHTML = html;
  document.querySelectorAll<HTMLButtonElement>('.holding-toggle').forEach((button) => {
    button.addEventListener('click', () => {
      const groupId = button.dataset.group;
      const key = button.dataset.key;
      if (!groupId || !key) return;
      const children = document.querySelectorAll<HTMLElement>(`[data-parent="${groupId}"]`);
      const expanded = button.getAttribute('aria-expanded') === 'true';
      children.forEach((row) => row.classList.toggle('hidden', expanded));
      button.setAttribute('aria-expanded', String(!expanded));
      button.setAttribute('aria-label', expanded ? 'Expand account details' : 'Collapse account details');
      if (expanded) state.expandedHoldings.delete(key);
      else state.expandedHoldings.add(key);
    });
  });

  updateHoldingSortIndicators();
}

export function exportResults(): void {
  if (state.results.length === 0) return;
  const headers = ['Account', 'Ticker', 'ISIN', 'Name', 'Quantity', 'AssetClass', 'ResolvedTicker', 'Price', 'PriceCurrency', 'FX', 'Value', 'Source', 'Error'];
  const lines = [headers.join(',')];

  for (const row of state.results) {
    lines.push([
      row.Account,
      row.Ticker,
      row.ISIN,
      row.Name,
      row.quantity,
      row.resolvedAssetClass,
      row.symbol,
      row.price,
      row.currency,
      row.fx,
      row.value,
      row.source,
      row.error,
    ].map(escapeCsv).join(','));
  }

  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
  anchor.download = 'portfolio_results.csv';
  anchor.click();
  URL.revokeObjectURL(anchor.href);
}

export function initialisePortfolio(): void {
  byId<HTMLInputElement>('fileInput').addEventListener('change', (event) => {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (file) void loadFile(file).catch((error) => showError(errorMessage(error)));
  });
  byId<HTMLButtonElement>('analyseButton').addEventListener('click', () => { void analyse(); });
  byId<HTMLButtonElement>('exportButton').addEventListener('click', exportResults);
  byId<HTMLButtonElement>('copyDiagnosticsButton').addEventListener('click', () => { void copyDiagnostics(); });

  // Click (or Enter/Space) a Holdings header to sort by that column; click again
  // to flip the direction.
  holdingHeaders().forEach((th, index) => {
    th.classList.add('sortable');
    th.tabIndex = 0;
    const activate = (): void => {
      holdingSort = holdingSort.column === index
        ? { column: index, dir: holdingSort.dir === 'asc' ? 'desc' : 'asc' }
        : { column: index, dir: NUMERIC_COLUMNS.has(index) ? 'desc' : 'asc' };
      if (holdingsContext) renderHoldings(holdingsContext.currency, holdingsContext.total);
    };
    th.addEventListener('click', activate);
    th.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(); }
    });
  });

  void checkApi();
}
